import React, { useCallback, useMemo, useState } from "react";

import { createFormatterForProfileType } from "@platformsh/observability-graph/ContinuousProfiling";
import { BaseFormatter } from "@platformsh/observability-graph/Formatter";
import * as d3 from "d3";

import {
  ObservabilityPipelineContinuousProfilingTimelineData,
  ObservabilityPipelineContinuousProfilingTimelinePoint
} from "Api/observability/pipeline/types";
import NoData from "src/organization/pages/project/environment/metrics/http/components/nodata";
import { defaultLayoutDimensions } from "src/organization/pages/project/environment/metrics/http/config";
import {
  adaptCursorData,
  getCursorDataFunc,
  getDefaultDomain,
  getLinearAxis
} from "src/organization/pages/project/environment/metrics/http/helper";
import useUpdateDimension from "src/organization/pages/project/environment/metrics/http/hook/useUpdateDimension";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import useConprofContext from "../hooks/useConprofContext";
import { ComparisonSide, ComparisonTimeFrame } from "../types";

import * as S from "./styles";
import TimelineLine from "./TimelineLine";
import TimelineSelector from "./TimelineSelector";
import TimelineTooltip from "./TimelineTooltip";

const chartId = "conprof-timeline";

const getDomains = (
  timelineData?: ObservabilityPipelineContinuousProfilingTimelineData,
  timeframe?: TimeFrameType
) => {
  const defaultDomain = getDefaultDomain(timeframe);
  if (!timelineData) {
    return defaultDomain;
  }

  return {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, d3.max(timelineData.points.map(point => point.value ?? 0))]
  };
};

const defaultDimension = {
  ...defaultLayoutDimensions,
  height: S.TimelineHeight
};

type Props = {
  comparisonTimeframe?: ComparisonTimeFrame;
  onComparisonTimeframeChanged?: (newValue: ComparisonTimeFrame) => void;
};

const Timeline = ({
  comparisonTimeframe,
  onComparisonTimeframeChanged
}: Props) => {
  const { timeframe, cursorData, setCursorData, data } = useConprofContext();
  const timelineData = data?.timelineData;
  const profileType = data?.profileType;

  const [highlightedSide, setHighlightedSide] = useState<ComparisonSide>("A");
  const dimension = useUpdateDimension(chartId, defaultDimension);
  const { xDomain, yDomain } = useMemo(
    () => getDomains(timelineData, timeframe),
    [timeframe, timelineData]
  );

  const { xScale, yScale, positions } = getLinearAxis({
    dimension,
    xDomain,
    yDomain
  });

  const tickX = getXTickCount(dimension.width, timeframe);
  const noData = timelineData?.points.length === 0;

  const formatter: BaseFormatter = useMemo(
    () =>
      profileType
        ? createFormatterForProfileType(profileType)
        : new BaseFormatter(),
    [profileType]
  );

  const cursorDataFuncProps = useMemo(
    () => ({
      xScale,
      yScale,
      data: timelineData?.points ?? [],
      setCursorData,
      bisectFn: (
        point: ObservabilityPipelineContinuousProfilingTimelinePoint
      ) => point.date,
      disable: noData,
      chartId
    }),
    [xScale, yScale, timelineData?.points, setCursorData, noData]
  );

  const memoCursorData = useMemo(
    () =>
      adaptCursorData({
        cursorFuncProps: cursorDataFuncProps,
        cursorData
      }),
    [cursorData, cursorDataFuncProps]
  );
  const handleComparisonSideATimeframeChanged = useCallback(
    sideA => {
      if (!comparisonTimeframe || !onComparisonTimeframeChanged) {
        return;
      }
      onComparisonTimeframeChanged({
        ...comparisonTimeframe,
        sideA
      });
    },
    [comparisonTimeframe, onComparisonTimeframeChanged]
  );
  const handleComparisonSideBTimeframeChanged = useCallback(
    sideB => {
      if (!comparisonTimeframe || !onComparisonTimeframeChanged) {
        return;
      }
      onComparisonTimeframeChanged({
        ...comparisonTimeframe,
        sideB
      });
    },
    [comparisonTimeframe, onComparisonTimeframeChanged]
  );
  const handleHighlightA = useCallback(() => {
    setHighlightedSide("A");
  }, []);
  const handleHighlightB = useCallback(() => {
    setHighlightedSide("B");
  }, []);

  const timelineSelectorA = comparisonTimeframe ? (
    <TimelineSelector
      xScale={xScale}
      yScale={yScale}
      height={dimension.height}
      side="A"
      comparisonSideTimeframe={comparisonTimeframe.sideA}
      onComparisonSideTimeframeChanged={handleComparisonSideATimeframeChanged}
      onHighlight={handleHighlightA}
    />
  ) : null;
  const timelineSelectorB = comparisonTimeframe ? (
    <TimelineSelector
      xScale={xScale}
      yScale={yScale}
      height={dimension.height}
      side="B"
      comparisonSideTimeframe={comparisonTimeframe.sideB}
      onComparisonSideTimeframeChanged={handleComparisonSideBTimeframeChanged}
      onHighlight={handleHighlightB}
    />
  ) : null;

  return (
    <S.Container>
      <S.Layout
        chartId={chartId}
        xScale={xScale}
        yScale={yScale}
        positions={positions}
        tick={{
          x: tickX,
          y: { format: (value: number) => formatter.toCondensedString(value) }
        }}
        onMouseMove={getCursorDataFunc(cursorDataFuncProps)}
        onMouseLeave={() => setCursorData?.()}
        cursorData={cursorData}
        showBrush={!comparisonTimeframe}
      >
        {noData || !timelineData || !profileType ? (
          <NoData />
        ) : (
          <TimelineLine
            xScale={xScale}
            yScale={yScale}
            points={timelineData.points ?? []}
            profileTypeName={profileType.name}
          />
        )}
        {/* move the highlighted selector on top of the other one. In order to keep the mouseover events*/}
        {highlightedSide === "B" ? (
          <>
            {timelineSelectorA}
            {timelineSelectorB}
          </>
        ) : (
          <>
            {timelineSelectorB}
            {timelineSelectorA}
          </>
        )}
      </S.Layout>
      <TimelineTooltip enabled={memoCursorData?.chartId === chartId} />
    </S.Container>
  );
};

export default Timeline;
